import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Loader from '../../components/loader';
import InvalidPublicShare from '../../publicshare/InvalidPublicShare';
import { ValidationType } from '../../publicshare/publicshare-types';
import { PublicForm } from '../../types/form-types';
import FormEditor, { FormEditorProps } from './FormEditor';
import PublicFormVerification from './PublicFormVerification';
import usePublicFormSession from '../../publicshare/hooks/usePublicFormSession';
import useCustomTheme from '../../hooks/useCustomTheme';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { ConfigState } from '../../publicshare/publicformwhitelabel/PublicFormWhitelabel';
import { customCss } from '../publicFormCustomTheme';

export interface PublicFormProps extends FormEditorProps {
  sharedAccessToken?: string;
  publicFormData?: PublicForm;
}

export default function PublicForm(props: PublicFormProps) {
  // flow:
  //   - make a request for the verification type
  //   - first request should be with a `null` secret
  //   - if verification is required, ask for the secret from the user
  //   - make another request with the secret to get a session

  const { sharedAccessToken: paramToken } = useParams<{ sharedAccessToken: string }>();
  const sharedAccessToken = props.sharedAccessToken || paramToken;
  const [secret, setSecret] = useState<string>(null);
  const { loading: sessionLoading, session: sessionInfo, error: sessionError } = usePublicFormSession(sharedAccessToken, secret);

  const config = useAppSelector<ConfigState>((state) => state.publicFormPersistedState?.configs);
  const themeCSS = useMemo(() => customCss(config), [config]);
  useCustomTheme(themeCSS, 'public-form-custom-theme', config?.brandColor, config?.faviconImage);

  // if there is an error, show the invalid public share component
  if (!sessionInfo && !!sessionError) {
    return <InvalidPublicShare errorMessage={sessionError.message} />;
  }

  if (!sessionInfo) {
    return <Loader isLoading />;
  }

  if ((sessionInfo.validationType !== ValidationType.None || sessionInfo.verificationRequired) && !sessionInfo.id) {
    return (
      <PublicFormVerification
        validationType={sessionInfo.validationType}
        recipientName={sessionInfo.recipientName}
        lastSecretAttempt={secret}
        postSecret={setSecret}
        loading={sessionLoading}
        validationError={sessionError?.status === 401 && sessionError?.message === 'The verification code you entered is not valid.'}
      />
    );
  }

  // if we made it here, we have passed verification
  return <FormEditor {...props} sessionId={sessionInfo.id} sharedAccessToken={sharedAccessToken} publicFormData={props.publicFormData} />;
}
